/*
---state samples:
use state:
	const state = Store.useState()

use state value:
	const value = Store.use('value')
	//or
	const value = Store.useState(state => state.value)
	//or
	const value = Store.useState().value

use multiple state values:
	const [value, data] = Store.use(['value', 'data'])
	//or
	const [value, data] = Store.useState(state => [state.value, state.data])

get state value:
	const value = Store.get().value		//= Store.useState.getState().value

set state value(s):
	Store.set({value: 1})				//= Store.useState.setState({value: 1})

switch state value(s):
	Store.set({active: !Store.get().active})

set cache value:
	Store.cache.setItem('data', jsonData)

get cache value:
	Store.cache.getItem('data')

action when value changed:
	const value = Store.use('value')
	useEffect(() => {	//fired after rerender
		console.log('---value changed!', value)
		/// your action here
	}, [value])

event listener for value updates:
	Store.on('value', (state, old) => {
		console.log('---value update:', state, old)
	})

event listener for all state updates:
	Store.onChange((state, old) => {
		console.log('---state update:', state, old)
	})


---extends this for your custom store:
import StoreBase from './lib.r3/StoreBase'

//============================================================================= Store
class Store extends StoreBase{
	async init(){
		this.set({
			isOnline	: false,
			isLoaded	: false,
		})
		await super.init(global.config)

		this.cache.setItem('version', this.config.version)
	}
}

let store = new Store()
export default store
*/

import { useState, useEffect } from "react";
import {create} from 'zustand'		// https://github.com/pmndrs/zustand
import { useShallow } from 'zustand/react/shallow'		//needs zustand version 4.4.6
import LocalCache from '../lib.r2/utils/LocalCache'

// const version = '1.0.3'
// const release = '16.11.2023'


//============================================================================= AppStore
export default class StoreBase{
	shallow = true
	cache = null
	runtime = window.require? 'node' : 'web'

	useState = create(() => ({
		isInit		: false,
	}))

	// set = state => this.useState.setState(state)
	set = (state, value) => {
		if (typeof state === 'string') state = {[state]:value}
		this.useState.setState(state)
	}

	get = this.useState.getState

	use = function(key){
		if (arguments.length === 1 && typeof key === 'string'){
			return this.useState(state => state[key])
		}
		return this.useState(useShallow(state => {
			let args = Array.isArray(key)? key : Array.from(arguments)
			return args.map(e => state[e])
		}))
	}

	#eventhandler = []
	#subscriber = null
	on = (key, fnc) => {		//add event handler
		this.#eventhandler.push([key, fnc])
		if (!this.#subscriber) this.#initSubscriber()
	}

	onChange = (fnc) => {
		this.#eventhandler.push(['__change', fnc])
		if (!this.#subscriber) this.#initSubscriber()
	}

	// ------------------------------------------------------------------------ #initSubscriber
	#initSubscriber(){
		this.#subscriber = this.useState.subscribe((state, old) => {
			this.#eventhandler.forEach(([key, fnc]) => {
				if (!fnc) return
				if (key === '__change') fnc(state, old)
				else if (state[key] !== old[key]) fnc(state[key], old[key])
			})
		})
	}

	// ------------------------------------------------------------------------ init Store
	async init(config){
		if (!config){
			console.error('no config defined!')
			return
		}
		this.config = config
		console.log('---init store:', (config.name || config.appName || ''), config.version || '')

		return new Promise((resolve, reject) => {
			this.cache = new LocalCache(config.name || config.appName || 'app-store', null, async () => {

				let lastSession = await this.cache.getItem('lastSession')
				let sessionStart = Date.now()
				await this.cache.setItem('lastSession', sessionStart)

				this.set({isInit:true, lastSession, sessionStart})

				resolve()
			})
		});
	}
}



// ---------------------------------------------------------------------------- useFetch
export const useFetch = (url) => {
	const [data, setData] = useState(null);

	useEffect(() => {
		fetch(url)
		.then((res) => res.json())
		.then((data) => setData(data))
	}, [url])

	return [data]
}



/* --- sample custom Store implementation:
// app.js:
export default function App() {
	useMemo(e => Store.init(), [])
	...


// Store.js:
import StoreBase from "./lib.r3/StoreBase";


class Store extends StoreBase {		//custom app store
	async init(){
		super.init(global.config)

		this.set({
			isOnline:false,
			value1:0,
			value2:0
		})		//set custom states here

		this.cache.setItem('version', this.config.version)		//cache test value

		///
	}

	
	// ------------------------------------------------------------------------ custom operations
	test(){
		const state = this.get()
		console.log('---test', state)
	}
}

export default new Store() 
*/
